import { CheckmarkIc } from '@dsch/dd-icons';
import { theme } from 'helpers/Theme';

import * as Styles from 'components/Toolkit/UpsellCard/styles';
import { accessibleOnClick } from 'utils/Accessibility';

import { ButtonLink } from 'components/Toolkit/Button/ButtonLink';

interface ISellingPoint {
  title: string;
  description?: string;
  toolTip?: {
    title: string;
    description: string;
  };
}

type TVariant = 'DEFAULT' | 'WIDE';
type TCTAAttribute = 'button' | 'link';

export interface IUpsellCard {
  selected: boolean;
  ctaText: string;
  lines?: ISellingPoint[];
  title: string;
  price?: string;
  onCta?: () => void;
  strength: number;
  error?: boolean;
  handleErrorMessage?: () => void;
  variant?: TVariant;
  arrayLength?: number;
  dataTestId?: string;
  isWeRecommend?: boolean;
  className?: string;
  ctaAttribute?: TCTAAttribute;
  paymentUrl?: string;
}

function UpsellCard({
  selected,
  ctaText,
  lines,
  variant = 'DEFAULT',
  onCta,
  dataTestId,
  title,
  error = false,
  strength,
  arrayLength,
  price,
  isWeRecommend,
  className,
  ctaAttribute = 'link',
  paymentUrl,
}: IUpsellCard) {
  const buttonText = selected ? (
    <>
      <CheckmarkIc color={theme.colors.BLUE} />
      <Styles.ButtonText>Selected</Styles.ButtonText>
    </>
  ) : (
    ctaText
  );

  return (
    <Styles.Border
      selected={selected}
      hasError={error}
      variant={variant}
      className={className}
    >
      <Styles.Wrapper
        data-testid={dataTestId ?? title}
        selected={selected}
        hasError={error}
        title={title}
        variant={variant}
        isWeRecommend={isWeRecommend}
      >
        {isWeRecommend && (
          <Styles.WeRecommendBanner hasError={error} variant={variant}>
            Recommended
          </Styles.WeRecommendBanner>
        )}
        <Styles.Content
          selected={selected}
          hasError={error}
          variant={variant}
          isWeRecommend={isWeRecommend}
        >
          <div>
            <Styles.FlexBox>
              <Styles.Title>{title}</Styles.Title>
              <Styles.Price>{price}</Styles.Price>
            </Styles.FlexBox>
            <Styles.SPowerBars strength={strength} arrayLength={arrayLength} />
            <Styles.SBulletPointList points={lines} />
          </div>
          {!paymentUrl && onCta ? (
            <Styles.SButton
              ofType="SECONDARY"
              isFakeButton={ctaAttribute !== 'button'}
              selected={selected}
              {...accessibleOnClick(onCta, ctaAttribute)}
            >
              {buttonText}
            </Styles.SButton>
          ) : undefined}
          {paymentUrl ? (
            <ButtonLink ofType="SECONDARY" href={paymentUrl}>
              {buttonText}
            </ButtonLink>
          ) : undefined}
        </Styles.Content>
      </Styles.Wrapper>
    </Styles.Border>
  );
}

export { UpsellCard };
